/**
 * GLM/Codex flat-rate boost_multiplier — same calibration as ClaudeWalletPanel's
 * (real subscription cost vs metered-equivalent value of captured usage).
 *
 * UPDATED 2026-08-24 — Codex is now LIVE too (codex-turn-ledger,
 * glm_quota_estimate_masked_exhausted_account_2026_08_22 follow-up)
 * -----------------------------------------------------------------------
 * GLM: `glm_quota_poller.py` writes a real, documented COUNT-based weekly_pct
 * estimate (Z.ai's API exposes no rate-limit-shaped headers to poll directly —
 * see that module's docstring for the honest-estimate methodology), and
 * `compute_flat_rate_boost_reconciliation` (server-side) derives a synthetic
 * trailing-7-day window for it since GLM's estimate is rolling, not tied to a
 * fixed reset date.
 *
 * Codex: `CodexRateLimitPoller` already writes a real weekly_pct from the
 * vendor's own rate-limit API (not an estimate); the piece that was actually
 * missing was `spend_usd` — every codex TaskUsageRow left it on the Claude-
 * transcript-scan estimate ("simply wrong" for this provider, see
 * TaskUsageRow.reasoning_tokens's own docstring), and no rate card existed for
 * the served model ("gpt-5.6-luna") to price against even if it had been real.
 * Both are fixed now: `_record_done_task_usage`'s codex override prices real
 * ledger usage via `model_pricing.price_usage()`. A row below computes a real
 * number once that account has both a weekly_pct reading and at least one
 * fully-priced task in its window — `boostReasonNoData` below explains
 * precisely which piece is still missing when it can't, IDENTICALLY for both
 * providers now (no more provider-specific special case).
 *
 * Per-provider aggregate rows: GLM's registered accounts sharing ONE Coding Plan
 * subscription (same `billing_group_key`) are aliases for one real bill, not one
 * each. CORRECTED 2026-08-30 (glm_second_subscription_account_billing_split_
 * 2026_08_30, operator: "it's only the per-account spend... it doesn't matter what
 * model we're using"): the backend (`compute_flat_rate_boost_reconciliation`) no
 * longer divides the price into a per-account "fair share" — every account sharing
 * a `billing_group_key` now carries the SAME combined (undivided price, summed
 * spend) result already. `computeProviderAggregates` below therefore dedupes by
 * `billing_group_key` (falling back to `account_id` when null, i.e. an unpriced
 * account with no tracked subscription) before summing — counting or summing a
 * shared bill once per ALIAS instead of once per REAL account would inflate a
 * provider's total by however many aliases share it.
 */

import { useEffect, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import { LIFETIME_WINDOW_HOURS } from "./DeepSeekWalletPanel";
import type { FlatRateBoostReconciliationView, FlatRateBoostWindowReconciliationView } from "./types";

const POLL_INTERVAL_MS = 60000;

// 1h/5h/24h/7d/30d/90d — same window set as ClaudeWalletPanel/DeepSeekWalletPanel
// (glm_second_subscription_account_billing_split_2026_08_30's window-slicing-parity
// follow-up: GLM/Codex get history-sampled at the SAME ~30-min UsagePoller cadence as
// Claude, via the provider-agnostic snapshot_account_usage_history(), so the identical
// delta-across-resets approach applies here too).
const WINDOW_OPTIONS: { hours: number; label: string }[] = [
  { hours: 1, label: "1h" },
  { hours: 5, label: "5h" },
  { hours: 24, label: "24h" },
  { hours: 168, label: "7d" },
  { hours: 720, label: "30d" },
  { hours: LIFETIME_WINDOW_HOURS, label: "90d" },
];

function fmtUsd(n: number | null): string {
  return n === null ? "—" : `$${n.toFixed(2)}`;
}

/** Precise reason a row's boost_multiplier is null — identical logic for every
 * provider now that both GLM and Codex have a real weekly_pct poller and a
 * real per-task $ price. */
export function boostReasonNoData(r: FlatRateBoostReconciliationView): string {
  if (r.weekly_pct === null && r.boost_multiplier_estimated) {
    return "no data yet — no completed tasks in the credit-estimate window yet";
  }
  if (r.weekly_pct === null) return "no data yet — awaiting this account's first weekly_pct reading";
  if (r.unpriced_turns > 0) return `no data yet — ${r.unpriced_turns} unpriced turn(s) in this window`;
  if (r.metered_equivalent_spend_usd === null) return "no data yet — no completed tasks in this window yet";
  return "no data yet";
}

/** Tooltip text for a row whose window is the synthetic `now - 7d` fallback (no real
 * vendor-reported reset/window-start data yet) rather than an actual observed
 * boundary — null when the window is real, so a caller can gate the caveat styling
 * on this being non-null (ao_billing_provider_aggregate_residuals_2026_08_31 item 6). */
export function syntheticWindowCaveat(r: FlatRateBoostReconciliationView): string | null {
  if (!r.synthetic_window) return null;
  return (
    "Estimated window — no real reset/window-start data reported yet for this subscription; this figure is a " +
    "synthetic trailing-7-day fallback, not a measured one."
  );
}

interface ProviderAggregate {
  provider: string;
  accountCount: number;
  monthlyTotalUsd: number;
  subscriptionSpendUsd: number | null;
  meteredEquivalentSpendUsd: number | null;
  boostMultiplier: number | null;
  /** Any contributing row's denominator is an AO-ledger estimate, not a vendor reading. */
  estimated: boolean;
}

/** The subset of either row shape (current-cycle or windowed) that aggregation
 * needs — lets one function serve both `FlatRateBoostPanel`'s current-cycle table
 * and its windowed table without duplicating the dedup-then-sum logic. */
interface FlatRateAggregatableRow {
  account_id: string;
  provider: string;
  billing_group_key: string | null;
  monthly_price_usd: number | null;
  subscription_spend_usd: number | null;
  metered_equivalent_spend_usd: number | null;
  /** Only the current-cycle row shape carries it; the windowed shape is always measured. */
  boost_multiplier_estimated?: boolean;
}

/** Why a windowed row has no multiplier yet, or a caveat about the window it
 * reflects — mirrors ClaudeWalletPanel's `claudeWindowRowCaveat`. */
export function flatRateWindowRowCaveat(row: FlatRateBoostWindowReconciliationView): string | null {
  if (row.monthly_price_usd === null) return "No tracked flat-rate subscription for this account — unpriced";
  if (row.start_sample_at === null || row.end_sample_at === null) {
    return "No usage samples reach back this far yet for this account";
  }
  if (row.unpriced_turns > 0) {
    return `${row.unpriced_turns} unpriced turn(s) in this window — metered-equivalent $ withheld`;
  }
  if (row.metered_equivalent_spend_usd === null) return "No captured spend yet this window";
  if (row.reset_crossed) {
    return "Window crosses a weekly reset — the delta is chained correctly across the reset.";
  }
  return null;
}

/** Sums subscription_spend_usd/metered_equivalent_spend_usd across every REAL
 * account of one provider (deduped by `billing_group_key`, falling back to
 * `account_id` when null), then divides ONCE — a blended multiplier, not an
 * average of per-account multipliers (which would silently over-weight a
 * low-usage account). A group whose combined result is still "no data"
 * (either side null) contributes ONLY its monthly_price_usd to the total —
 * never a partial/fabricated $ figure — so the aggregate degrades to the same
 * boostReasonNoData explanation once EVERY real account in the provider has
 * real numbers to sum. Order follows first appearance in `rows` (glm before
 * codex today, matching the API's own `providers` default). Works identically
 * for the current-cycle and windowed row shapes (see `FlatRateAggregatableRow`). */
export function computeProviderAggregates(rows: FlatRateAggregatableRow[]): ProviderAggregate[] {
  const order: string[] = [];
  const byProvider = new Map<
    string,
    {
      accountCount: number;
      monthlyTotalUsd: number;
      subscriptionTotal: number;
      meteredTotal: number;
      complete: boolean;
      estimated: boolean;
      seenGroups: Set<string>;
    }
  >();
  for (const r of rows) {
    if (!byProvider.has(r.provider)) {
      order.push(r.provider);
      byProvider.set(r.provider, {
        accountCount: 0,
        monthlyTotalUsd: 0,
        subscriptionTotal: 0,
        meteredTotal: 0,
        complete: true,
        estimated: false,
        seenGroups: new Set(),
      });
    }
    const bucket = byProvider.get(r.provider)!;
    // Every account sharing one billing_group_key carries the IDENTICAL combined
    // result already (see compute_flat_rate_boost_reconciliation) — count/sum it
    // only once per REAL account, not once per alias.
    const dedupKey = r.billing_group_key ?? r.account_id;
    if (bucket.seenGroups.has(dedupKey)) continue;
    bucket.seenGroups.add(dedupKey);
    bucket.accountCount += 1;
    bucket.monthlyTotalUsd += r.monthly_price_usd ?? 0;
    if (r.subscription_spend_usd !== null && r.metered_equivalent_spend_usd !== null) {
      bucket.subscriptionTotal += r.subscription_spend_usd;
      bucket.meteredTotal += r.metered_equivalent_spend_usd;
      if (r.boost_multiplier_estimated) bucket.estimated = true;
    } else {
      bucket.complete = false;
    }
  }
  return order.map((provider) => {
    const b = byProvider.get(provider)!;
    const hasReal = b.complete && b.subscriptionTotal > 0;
    return {
      provider,
      accountCount: b.accountCount,
      monthlyTotalUsd: b.monthlyTotalUsd,
      subscriptionSpendUsd: b.complete ? b.subscriptionTotal : null,
      meteredEquivalentSpendUsd: b.complete ? b.meteredTotal : null,
      boostMultiplier: hasReal ? b.meteredTotal / b.subscriptionTotal : null,
      estimated: b.estimated,
    };
  });
}

export function FlatRateBoostPanel({ baseUrl }: { baseUrl: string }) {
  const [rows, setRows] = useState<FlatRateBoostReconciliationView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Windowed view (glm_second_subscription_account_billing_split_2026_08_30's
  // window-slicing-parity follow-up, matching ClaudeWalletPanel's window set) — a
  // SEPARATE fetch/poll from the current-cycle `rows` above, re-fetched whenever the
  // toggle selection changes.
  const [windowHours, setWindowHours] = useState(WINDOW_OPTIONS[0].hours);
  const [windowRows, setWindowRows] = useState<FlatRateBoostWindowReconciliationView[] | null>(null);
  const [windowError, setWindowError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.getFlatRateBoostReconciliation(baseUrl);
        if (!cancelled) {
          setRows(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  useEffect(() => {
    let cancelled = false;
    const loadWindow = async () => {
      try {
        const result = await api.getFlatRateBoostWindowReconciliation(baseUrl, windowHours);
        if (!cancelled) {
          setWindowRows(result);
          setWindowError(null);
        }
      } catch (e) {
        if (!cancelled) setWindowError(e instanceof Error ? e.message : String(e));
      }
    };
    void loadWindow();
    const id = setInterval(() => void loadWindow(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, windowHours]);

  const aggregates = rows ? computeProviderAggregates(rows) : [];
  const windowAggregates = windowRows ? computeProviderAggregates(windowRows) : [];

  return (
    <Panel title="GLM/Codex Subscription Value" collapsible tone="accounts">
      <div className="usage-panel-hint">
        Same calibration as Claude's (subscription cost vs metered-equivalent value of captured usage) — normalized into
        $/token spend implied by each account's weekly_pct-of-limit reading. Both GLM and Codex have a real poller now
        and compute this once an account has both a weekly_pct reading and a fully-priced task in its window. A Z.ai
        team seat exposes no vendor reading at all, so its row uses the AO credit ledger instead and is labelled (est.)
        — never treat that as a measured multiplier. Each provider's own accounts are separate subscriptions (not one
        split by model) — the bolded row above each group blends them into one multiplier; the rows below it are the
        real per-account numbers that sum to it.
      </div>
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {!error && !rows && <div className="task-usage-pending">loading…</div>}
      {!error && rows && rows.length === 0 && (
        <div className="task-usage-pending">no GLM/Codex accounts registered</div>
      )}
      {!error && rows && rows.length > 0 && (
        <div className="gemini-capacity-accounts">
          {aggregates.map((agg) => (
            <div key={`agg-${agg.provider}`}>
              <div className="capacity-account capacity-account-aggregate">
                <div className="capacity-account-header">
                  <span className="mono">
                    <strong>{agg.provider}</strong> ({agg.accountCount} account{agg.accountCount === 1 ? "" : "s"})
                  </span>
                  <span className="capacity-account-meta">{fmtUsd(agg.monthlyTotalUsd)}/mo total</span>
                </div>
                <table className="acc-usage-table acc-usage-table-breakdown">
                  <tbody>
                    <tr>
                      <td className="acc-usage-window-label">
                        <strong>Blended boost multiplier</strong>
                      </td>
                      <td className="mono" colSpan={2}>
                        <strong>
                          {agg.boostMultiplier === null
                            ? "no data yet — not every account in this group has a priced window yet"
                            : `${agg.boostMultiplier.toFixed(2)}x${agg.estimated ? " (est.)" : ""}`}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {rows
                .filter((r) => r.provider === agg.provider)
                .map((r) => (
                  <div key={r.account_id} className="capacity-account capacity-account-nested">
                    <div className="capacity-account-header">
                      <span className="mono">{r.label}</span>
                      <span className="capacity-account-meta">
                        {r.provider} · {fmtUsd(r.monthly_price_usd)}/mo
                      </span>
                    </div>
                    <table className="acc-usage-table acc-usage-table-breakdown">
                      <tbody>
                        <tr>
                          <td
                            className={`acc-usage-window-label${r.synthetic_window ? " acc-usage-caveat" : ""}`}
                            title={syntheticWindowCaveat(r) ?? undefined}
                          >
                            Boost multiplier
                          </td>
                          <td className="mono" colSpan={2}>
                            {r.boost_multiplier === null ? boostReasonNoData(r) : `${r.boost_multiplier.toFixed(2)}x`}
                            {r.boost_multiplier_estimated ? " (est. — AO credit ledger, not vendor-measured)" : ""}
                            {r.synthetic_window ? " ≈ (estimated window)" : ""}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}
      <div className="usage-panel-hint">
        Windowed view: the same ratio bounded by an explicit window instead of each group's own current cycle, so
        different accounts (different reset days) are comparable over the SAME wall-clock span.
      </div>
      <div className="task-usage-provider-toggle">
        {WINDOW_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            className={`btn btn-xs ${windowHours === opt.hours ? "active" : ""}`}
            onClick={() => setWindowHours(opt.hours)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {windowError && <div className="task-usage-error">Failed to load window: {windowError}</div>}
      {!windowError && !windowRows && <div className="task-usage-pending">loading…</div>}
      {!windowError && windowRows && windowRows.length === 0 && (
        <div className="task-usage-pending">no GLM/Codex accounts registered</div>
      )}
      {!windowError && windowRows && windowRows.length > 0 && (
        <div className="gemini-capacity-accounts">
          {windowAggregates.map((agg) => (
            <div key={`agg-window-${agg.provider}`}>
              <div className="capacity-account capacity-account-aggregate">
                <div className="capacity-account-header">
                  <span className="mono">
                    <strong>{agg.provider}</strong> ({agg.accountCount} account{agg.accountCount === 1 ? "" : "s"})
                  </span>
                  <span className="capacity-account-meta">{fmtUsd(agg.monthlyTotalUsd)}/mo total</span>
                </div>
                <table className="acc-usage-table acc-usage-table-breakdown">
                  <tbody>
                    <tr>
                      <td className="acc-usage-window-label">
                        <strong>Blended boost multiplier</strong>
                      </td>
                      <td className="mono" colSpan={2}>
                        <strong>
                          {agg.boostMultiplier === null
                            ? "no data yet — not every account in this group has a priced window yet"
                            : `${agg.boostMultiplier.toFixed(2)}x${agg.estimated ? " (est.)" : ""}`}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {windowRows
                .filter((r) => r.provider === agg.provider)
                .map((r) => (
                  <div key={r.account_id} className="capacity-account capacity-account-nested">
                    <div className="capacity-account-header">
                      <span className="mono">{r.label}</span>
                      <span className="capacity-account-meta">
                        {r.provider} · {fmtUsd(r.monthly_price_usd)}/mo
                      </span>
                    </div>
                    <table className="acc-usage-table acc-usage-table-breakdown">
                      <tbody>
                        <tr>
                          <td
                            className={`acc-usage-window-label${r.reset_crossed ? " acc-usage-caveat" : ""}`}
                            title={flatRateWindowRowCaveat(r) ?? undefined}
                          >
                            Boost multiplier
                          </td>
                          <td className="mono" colSpan={2}>
                            {r.boost_multiplier === null
                              ? "no data yet"
                              : `${r.boost_multiplier.toFixed(2)}x${r.reset_crossed ? " ⚠" : ""}`}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
